import { AppDestroy, provideAppDestroy } from '@bcf-vanilla-ts-v1-shared/di/app-destroy';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import {
  DoRouterReplaceUrlData,
  DoRouterUpdateQueryParamsData,
  DoRouterUpdateUrlData
} from '@bcflit-v1-ui-shared/weak-listener/types';
import { weakListener } from '@bcflit-v1-ui-shared/weak-listener/weak-listener';
import { QueryParams } from '@tmf-shared-platform/activated-route/activated-route';
import { HistoryUrl } from '@tmf-shared-platform/history-url/history-url';
import { BcfLitRouter } from '@tmf-shared-platform/router/internal';
import { notFoundRoutePath } from '@tmf-ui-routes/paths/not-found.route-path';
import { isServer } from 'lit';
import { fromEvent, takeUntil } from 'rxjs';
import { routerGoBack, routerUpdateQueryParams, routerUpdateReplaceUrl, routerUpdateUrl } from './router-utils';

export class RouterNavigatorService {
  private _router: BcfLitRouter | undefined;
  private _historyUrl: HistoryUrl | undefined;

  constructor(private _appDestroy: AppDestroy) {
    this._initNavigators();
  }

  public bindRouter(router: BcfLitRouter): void {
    this._router ??= router;
    if (!isServer) {
      this._initWatcher();
    }
  }

  public bindHistoryUrl(historyUrl: HistoryUrl): void {
    this._historyUrl = historyUrl;
  }

  public navigateTo(path: string, queryParams?: QueryParams, state?: Record<string, any>): void {
    if (!this._router) {
      return;
    }
    routerUpdateUrl(this._router, path, queryParams, state);
  }

  public goBack(skipSameRoute: boolean = false): void {
    if (!this._historyUrl) {
      return;
    }
    routerGoBack(this._historyUrl, skipSameRoute);
  }

  private _initWatcher(): void {
    fromEvent(window, 'routerNotFound')
      .pipe(takeUntil(this._appDestroy.destroy$))
      .subscribe(() => {
        routerUpdateReplaceUrl(
          this._router!,
          notFoundRoutePath(), // + `?notFoundpathname=${window.location.pathname}`,
          undefined,
          { notFoundPath: window.location.pathname }
        );
      });
  }

  private _initNavigators(): void {
    weakListener('do-router-update-url', (data: DoRouterUpdateUrlData) => {
      routerUpdateUrl(this._router!, data.url, data.queryParams, data.state, data.fragment);
    });
    weakListener('do-router-update-query-params', (data: DoRouterUpdateQueryParamsData) => {
      if (data.strategy === 'merge') {
        routerUpdateQueryParams(new URLSearchParams(window.location.search), data.queryParams ?? {});
      } else {
        routerUpdateQueryParams(new URLSearchParams(''), data.queryParams ?? {});
      }
    });
    weakListener('do-router-replace-url', (data: DoRouterReplaceUrlData) => {
      routerUpdateReplaceUrl(this._router!, data.url, data.queryParams, data.state);
    });
    weakListener('router-back-from-open-game-to-game-list-category', () => {
      this.goBack();
    });
  }
}

export function provideRouterNavigatorService(): RouterNavigatorService {
  return provideSingleton(RouterNavigatorService, () => new RouterNavigatorService(provideAppDestroy()));
}
